import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useUser } from 'context/user/User';
import ordersApi from 'api/requests/orders';
import pusherService from 'services/pusher';
import useToast from 'hooks/useToast';
import ToastContainer from 'components/_UI/ToastsContainer';
import usePanelPermissions from 'hooks/usePanelPermissions';
import { PERMISSIONS } from 'helpers/panelPermissions';
import paths from 'routes/paths';

const RefundRequestsContext = createContext();

/** `?estornos-pendentes` abre Pedidos na aba dos pedidos de estorno esperando resposta. */
export const REFUND_REQUESTS_TAB_PARAM = 'estornos-pendentes';
export const REFUND_REQUESTS_PATH = `${paths.dashboard.orders}?${REFUND_REQUESTS_TAB_PARAM}`;

/**
 * Pedidos de estorno esperando o produtor responder: o banner, o contador da
 * sidebar e a aba de Pedidos leem daqui.
 *
 * A contagem vem da API (não do sino), então só cai quando alguém responde.
 * Atualiza ao montar (cada página do painel), ao voltar para a aba do navegador
 * e na hora pelo Pusher (`producers.{id}`). Quem entrou por uma equipe vê o
 * painel do produtor: `user` já é o produtor, e o canal é o dele.
 *
 * `version` muda a cada aviso do Pusher, para a lista de pedidos se recarregar.
 */
export default function RefundRequestsProvider({ children }) {
    const { user } = useUser();
    const { can, canOpen } = usePanelPermissions();
    const { toasts, toast } = useToast();
    const [pendingCount, setPendingCount] = useState(0);
    const [oldestPendingAt, setOldestPendingAt] = useState(null);
    const [version, setVersion] = useState(0);

    const producerId = user?.id;
    const enabled = !!producerId && canOpen(paths.dashboard.orders) && can(PERMISSIONS.ORDERS_MANAGE);

    const refresh = useCallback(async () => {
        if (!enabled) return;

        try {
            const { data } = await ordersApi.producerRefundRequests({ status: 'pending' });
            setPendingCount(data?.meta?.pending_count || 0);
            setOldestPendingAt(data?.meta?.oldest_pending_at || null);
        } catch (error) {
            // Sem a contagem o painel só não mostra o destaque; o sino continua avisando.
        }
    }, [enabled]);

    useEffect(() => {
        refresh();

        window.addEventListener('focus', refresh);

        return () => window.removeEventListener('focus', refresh);
    }, [refresh]);

    useEffect(() => {
        if (!enabled) return;

        const channelName = `producers.${producerId}`;

        const handleUpdated = (refundRequest) => {
            refresh();
            setVersion(current => current + 1);

            if (refundRequest?.status === 'pending') {
                toast.warning(`Novo pedido de estorno – pedido #${refundRequest.order_id}`);
            }
        };

        pusherService.subscribe(channelName, 'refund-request.updated', handleUpdated);

        return () => pusherService.unsubscribe(channelName, 'refund-request.updated', handleUpdated);
        // `toast` muda a cada render; o que importa para assinar é o canal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, producerId, refresh]);

    return (
        <RefundRequestsContext.Provider value={{ enabled, pendingCount, oldestPendingAt, version, refresh }}>
            <ToastContainer toasts={toasts} />
            {children}
        </RefundRequestsContext.Provider>
    );
}

// Seguro fora do provider (o painel do estabelecimento não o monta): devolve undefined.
export function useRefundRequestsOptional() {
    return useContext(RefundRequestsContext);
}
